import { Component, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
  FormGroupDirective,
  NgForm,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { UserRegistrationRequest, UserRole } from 'src/app/models/api/user';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  debounceTime,
  tap,
  flatMap,
  map,
  distinctUntilChanged,
  finalize,
  mergeMap,
  catchError,
} from 'rxjs/operators';
import { AvailabilityResponse } from 'src/app/models/api/common';
import { Observable } from 'rxjs';
import { UsernameAvailableValidator } from 'src/app/services/user/validators.service';
import { NotificationService } from 'src/app/services/notification/notification.service';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit {
  UserRole = UserRole;

  form: FormGroup;
  passwordFieldType = 'password';
  loading = false;
  role = UserRole.Student;

  emailPattern =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z0-9.-]+)?$/;
  supportedTLDs = 'com|org|net|edu|gov|co|io|name|info|biz|me|team';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private notificationService: NotificationService
  ) {
    this.form = this.formBuilder.group(
      {
        fullName: [null, [Validators.required, ValidNameMatcher]],
        email: [
          null,
          [
            Validators.required,
            Validators.email,
            this.customEmailValidator.bind(this),
          ],
        ],
        username: [null, [Validators.required, Validators.minLength(3)]],
        password: [null, [Validators.required, Validators.minLength(8)]],
        repeat: [null, []],
        referredBy: [null, []],
      },
      {
        // validator: this.checkPasswords
      }
    );
  }

  ngOnInit() {
    const referralCode = this.route.snapshot.queryParams.referralCode;

    if (referralCode) {
      this.form.get('referredBy').setValue(referralCode);
    }

    this.form.get('email').valueChanges.subscribe((value) => {
      const usernameValue = this.form.get('username').value;
      const noUsernameSet = this.form.get('username').pristine;

      if (noUsernameSet || !usernameValue || value.startsWith(usernameValue)) {
        this.form.get('username').setValue(value.split('@')[0]);
        this.form.get('username').markAsTouched();
      }
    });
  }

  extractTLD(email: string) {
    const domain = email?.split('@')[1];
    const tld = domain && domain.split('.').pop();
    return tld || null;
  }

  customEmailValidator(control: AbstractControl): ValidationErrors | null {
    const email = control.value as string;

    let isValidEmailPattern = this.emailPattern.test(email);
    let isValidEmailTLD = this.supportedTLDs
      .split('|')
      .includes(this.extractTLD(email));

    if (!isValidEmailPattern) return { invalidEmail: true };
    else if (!isValidEmailTLD) return { invalidEmail: true };
    else return null;
  }

  validateUserRegistration() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const registrationRequest: UserRegistrationRequest = {
      ...this.form.value,
    };
    const fullName = this.form.get('fullName').value ?? '';
    const splitFullName = fullName.trim().split(' ');

    if (splitFullName.length < 2) {
      this.form.markAllAsTouched();
      return;
    }

    registrationRequest.firstName = splitFullName[0];
    registrationRequest.lastName = splitFullName[splitFullName.length - 1];
    registrationRequest.role = this.role;

    this.register(registrationRequest);

    // const isValidEmailAddress = this.isUserEmailValidated();
    // if (isValidEmailAddress) {
    //   this.register(registrationRequest);
    // } else {
    //   this.notificationService.showError('Invalid email address');
    // }
  }

  register(registrationRequest: any) {
    this.loading = true;
    this.authService
      .registerUser(registrationRequest)
      .pipe(
        flatMap(() =>
          this.authService.login({
            username: registrationRequest.email,
            password: registrationRequest.password,
          })
        ),
        finalize(() => (this.loading = false)),
        catchError((err) => {
          if (err.status === 409) {
            console.error('error registering user', registrationRequest, err);
            this.notificationService
              .showError(
                'An account with this email already exists. To continue, please login',
                5000,
                'Login'
              )
              .subscribe((_) => {
                this.router.navigateByUrl('/session/log-in');
              });
          }
          throw err;
        })
      )
      .subscribe((_) => {
        const returnUrl = this.route.snapshot.queryParams.returnUrl;
        this.router.navigateByUrl(returnUrl);
      });
  }

  checkPasswords(form: FormGroup) {
    const pass = form.controls.password.value;
    const confirmPass = form.controls.repeat.value;

    return pass === confirmPass ? null : { notSame: true };
  }

  get email() {
    return this.form.get('email');
  }
  get username() {
    return this.form.get('username');
  }
}

export function ValidNameMatcher(control: AbstractControl) {
  const splitFullName = (control.value ?? '').trim().split(' ');
  if (
    splitFullName.length < 2 ||
    splitFullName[0].length < 1 ||
    splitFullName[splitFullName.length - 1].length < 1
  ) {
    return {
      invalidName: true,
    };
  }

  return null;
}
